import React from 'react';

import { useIntl } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { addColumnBefore, tooltip } from '@atlaskit/editor-common/keymaps';
import { messages } from '@atlaskit/editor-common/messages/table';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { getSelectionRect } from '@atlaskit/editor-tables/utils/get-selection-rect';
import { TableColumnAddLeftIcon } from '@atlaskit/editor-toolbar/column-add-left-icon';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import { ToolbarKeyboardShortcutHint } from '@atlaskit/editor-toolbar/toolbar-keyboard-shortcut-hint';

import { closeActiveTableMenu } from '../../../../pm-plugins/commands';
import { insertColumnWithAnalytics } from '../../../../pm-plugins/commands/commands-with-analytics';
import type { TableSharedStateInternal } from '../../../../types';
import { useTableMenuContext } from '../../shared/TableMenuContext';
import type { TableMenuComponentsParams } from '../../shared/types';

export const AddColumnLeftItem = ({ api }: TableMenuComponentsParams): React.JSX.Element => {
	const { editorView } = useTableMenuContext() ?? {};
	const { formatMessage } = useIntl();
	const { isCommentEditor, isTableFixedColumnWidthsOptionEnabled, isTableScalingEnabled } =
		useSharedPluginStateWithSelector(api ?? undefined, ['table'], (states) => {
			const tableState = states.tableState as TableSharedStateInternal | undefined;
			return {
				isCommentEditor: tableState?.isCommentEditor,
				isTableFixedColumnWidthsOptionEnabled: tableState?.isTableFixedColumnWidthsOptionEnabled,
				isTableScalingEnabled: tableState?.isTableScalingEnabled,
			};
		});

	const handleClick = () => {
		if (!editorView) {
			return;
		}

		const selectionRect = getSelectionRect(editorView.state.selection);
		const index = selectionRect?.left;
		if (index === undefined) {
			return;
		}

		const shouldUseIncreasedScalingPercent =
			isTableScalingEnabled && (isTableFixedColumnWidthsOptionEnabled || isCommentEditor);

		insertColumnWithAnalytics(
			api,
			api?.analytics?.actions,
			isTableScalingEnabled,
			isTableFixedColumnWidthsOptionEnabled,
			shouldUseIncreasedScalingPercent,
			isCommentEditor,
		)(INPUT_METHOD.TABLE_CONTEXT_MENU, index)(editorView.state, editorView.dispatch, editorView);
		api?.core.actions.execute(closeActiveTableMenu(api));
		api?.core.actions.focus();
	};

	return (
		<ToolbarDropdownItem
			onClick={handleClick}
			elemBefore={<TableColumnAddLeftIcon color="currentColor" label="" size="small" />}
			elemAfter={<ToolbarKeyboardShortcutHint shortcut={tooltip(addColumnBefore) ?? ''} />}
		>
			{formatMessage(messages.addColumnLeft)}
		</ToolbarDropdownItem>
	);
};
